import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { InvoiceHeader, InvoiceListFilter } from '@/lib/api-types'
import { formatDate, formatMoney } from '@/lib/format'
import { paymentChipClasses } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { StatusChip } from '@/components/ui/status-chip'
import { Toolbar } from '@/components/ui/toolbar'

const FILTERS: InvoiceListFilter[] = ['open', 'paid', 'all']

const FILTER_LABELS: Record<InvoiceListFilter, string> = {
  open: 'Open',
  paid: 'Paid',
  all: 'All',
}

function parseFilter(raw: string | null): InvoiceListFilter {
  return raw === 'paid' || raw === 'all' ? raw : 'open'
}

const COLUMNS: DataTableColumn<InvoiceHeader>[] = [
  {
    id: 'number',
    header: 'Invoice #',
    widthClassName: 'w-28',
    cell: (inv) => (
      <Link
        to={`/invoices/${inv.id}`}
        onClick={(event) => event.stopPropagation()}
        className="rounded-sm font-mono text-[0.8125rem] text-inkMid no-underline underline-offset-4 hover:text-brand hover:underline"
      >
        {inv.docNum ?? '—'}
      </Link>
    ),
  },
  {
    // No natural free-text field on an invoice row — the status column takes
    // the slack (charter rule) with a floor so the chip never clips.
    id: 'status',
    header: 'Status',
    flex: true,
    widthClassName: 'min-w-36',
    cell: (inv) => (
      <StatusChip label={inv.paymentStatusLabel} chipClasses={paymentChipClasses(inv.paymentGroup)} />
    ),
  },
  {
    id: 'issued',
    header: 'Issued',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (inv) => <span className="text-sm text-inkMid">{formatDate(inv.docDate)}</span>,
  },
  {
    id: 'due',
    header: 'Due',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (inv) => (
      <span
        className={cn(
          'text-sm',
          inv.paymentGroup === 'overdue' ? 'font-medium text-amber' : 'text-inkMid',
        )}
      >
        {formatDate(inv.dueDate)}
      </span>
    ),
  },
  {
    id: 'total',
    header: 'Total',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (inv) => formatMoney(inv.docTotal, inv.currency),
  },
  {
    id: 'balance',
    header: 'Balance',
    align: 'right',
    widthClassName: 'w-32',
    nowrap: true,
    cell: (inv) => (
      <span className="font-medium text-ink">{formatMoney(inv.balanceDue, inv.currency)}</span>
    ),
  },
]

export function InvoicesListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = parseFilter(searchParams.get('status'))

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['invoices', filter],
    queryFn: ({ signal }) => api.listInvoices(filter, { signal }),
    staleTime: 15_000,
  })

  function setFilter(next: InvoiceListFilter) {
    const params = new URLSearchParams(searchParams)
    if (next === 'open') params.delete('status')
    else params.set('status', next)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="mx-auto max-w-data">
      <PageHeader title="Invoices" description="Balances, payments, and pay-by-card." />

      {data ? (
        <p className="mt-6 text-sm text-inkMid">
          <span className="micro-label">Open balance</span>{' '}
          <span className="ml-2 font-mono text-lg font-semibold text-ink">
            {formatMoney(data.openBalance, data.invoices[0]?.currency ?? 'USD')}
          </span>
        </p>
      ) : null}

      <Toolbar className="mt-5">
        <SegmentedControl
          label="Filter invoices"
          value={filter}
          onChange={setFilter}
          options={FILTERS.map((f) => ({ value: f, label: FILTER_LABELS[f] }))}
        />
      </Toolbar>

      <div className="mt-4">
        <DataTable<InvoiceHeader>
          columns={COLUMNS}
          rows={data?.invoices ?? []}
          rowKey={(inv) => inv.id}
          onRowClick={(inv) => navigate(`/invoices/${inv.id}`)}
          rowClassName={(inv) =>
            inv.paymentGroup === 'overdue' ? 'border-l-2 border-l-amber' : undefined
          }
          caption="Your invoices from Green Line"
          loading={isPending}
          loadingLabel="Loading your invoices…"
          error={
            isError ? (
              <ErrorState
                message="We couldn’t load your invoices."
                onRetry={() => void refetch()}
              />
            ) : undefined
          }
          empty={
            <EmptyState
              title={
                filter === 'open'
                  ? 'Nothing outstanding — you’re all settled up.'
                  : 'Invoices from Green Line will appear here.'
              }
            />
          }
          renderCard={(inv) => <InvoiceCard invoice={inv} />}
        />
      </div>
    </div>
  )
}

/** Narrow-screen fallback: one tappable card per invoice. */
function InvoiceCard({ invoice }: { invoice: InvoiceHeader }) {
  return (
    <Link to={`/invoices/${invoice.id}`} className="block no-underline">
      <Card
        padding="sm"
        className={cn(
          'transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream',
          invoice.paymentGroup === 'overdue' && 'border-l-2 border-l-amber',
        )}
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[0.8125rem] text-inkMid">{invoice.docNum ?? '—'}</span>
          <span className="font-mono text-[0.8125rem] text-inkMid">
            {formatMoney(invoice.balanceDue, invoice.currency)} due
          </span>
        </span>
        <span className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-mute">
          <StatusChip
            label={invoice.paymentStatusLabel}
            chipClasses={paymentChipClasses(invoice.paymentGroup)}
          />
          {invoice.dueDate ? <span>due {formatDate(invoice.dueDate)}</span> : null}
        </span>
      </Card>
    </Link>
  )
}
